import React, { useContext, useEffect, useState } from 'react';
import { areAddressesEquivalent } from '@broadleaf/payment-js';
import { find, get, isEmpty } from 'lodash';

import { AddressForm, Icon, LoadingIcon } from 'app/common/components';
import useCustomerAddresses from 'app/common/hooks/useCustomerAddresses.js';

import { AddressFormLegend, UserAddresses } from './components';
import { CheckoutContext } from 'app/checkout/contexts';
import { setSelectedAddress } from 'app/checkout/contexts/reducers/checkoutReducer';
import { useFormatMessage } from 'app/common/hooks';
import messages from './AuthenticatedAddressForm.messages';

const AuthenticatedAddressForm = props => {
  const { address, disabled, type } = props;
  const formatMessage = useFormatMessage();
  const [useSavedAddress, setUseSavedAddress] = useState(false);

  const { checkoutDispatch, selectedAddress, showAddressErrors } =
    useContext(CheckoutContext);

  const { combinedAddresses } = useCustomerAddresses();

  useEffect(() => {
    setUseSavedAddress(!showAddressErrors);
  }, [showAddressErrors]);

  function resetAddressSelect(defaultProp) {
    const defaultAddress = find(combinedAddresses, [defaultProp, true]);
    if (defaultAddress) {
      setSelectedAddress(checkoutDispatch, defaultAddress);
    } else {
      setSelectedAddress(checkoutDispatch, combinedAddresses[0]);
    }
  }

  useEffect(() => {
    if (isEmpty(combinedAddresses)) {
      return;
    }

    setUseSavedAddress(true);
    const selected = find(combinedAddresses, sa =>
      areAddressesEquivalent(sa, address)
    );

    if (!isEmpty(selected)) {
      setSelectedAddress(checkoutDispatch, selected);
    } else if (!isEmpty(address)) {
      setUseSavedAddress(false);
    } else {
      let defaultProp;
      if (type === AddressForm.Type.FULFILLMENT) {
        defaultProp = 'defaultShippingAddress';
      } else if (type === AddressForm.Type.BILLING) {
        defaultProp = 'defaultBillingAddress';
      }
      resetAddressSelect(defaultProp);
    }
    // eslint-disable-next-line
  }, [address, combinedAddresses, type]);

  if (!combinedAddresses) {
    return (
      <div className="text-center">
        <LoadingIcon />
      </div>
    );
  }

  const validAddress =
    useSavedAddress &&
    selectedAddress &&
    selectedAddress.firstName?.trim() &&
    selectedAddress.lastName?.trim() &&
    get(selectedAddress, 'phonePrimary.phoneNumber')?.trim();
  const isFleetAddress = get(selectedAddress || address, 'fleetAddress', false);
  return (
    <>
      {validAddress ? (
        <UserAddresses
          customerAddresses={combinedAddresses}
          selectedAddress={selectedAddress}
          setUseSavedAddress={setUseSavedAddress}
          type={type}
        />
      ) : (
        <>
          {isFleetAddress && (
            <FleetAddressCaveat formatMessage={formatMessage} />
          )}
          <AddressForm
            type={type}
            disabled={disabled}
            isFleetAddress={isFleetAddress}
            legend={
              <AddressFormLegend
                customerAddresses={combinedAddresses}
                type={type}
                handleChooseSavedAddress={() => {
                  setUseSavedAddress(true);
                  resetAddressSelect('defaultShippingAddress');
                }}
              />
            }
          />
        </>
      )}
    </>
  );
};

function FleetAddressCaveat({ formatMessage }) {
  return (
    <aside className="flex items-center mb-4 px-2 py-1 text-sm text-yellow-700 leading-snug border border-solid border-yellow-300 bg-yellow-100 rounded md:px-4 md:py-2 lg:text-base lg:leading-normal">
      <Icon className="mr-2 md:mr-4" name="info-circle" />
      {formatMessage(messages.fleetAddressMessage)}
    </aside>
  );
}

export default AuthenticatedAddressForm;
export { AuthenticatedAddressForm };
